import type {
  StorageLocation,
  StorageLocationQuery,
  CreateStorageLocationInput,
  UpdateStorageLocationInput,
} from '@maker-toolchest/shared';
import { api } from '@/lib/api';

export interface StorageLocationListResponse {
  data: StorageLocation[];
  meta?: {
    pagination: {
      page: number;
      limit: number;
      total: number;
      totalPages: number;
    };
  };
}

export const storageApi = {
  /**
   * List storage locations with optional filtering
   */
  list: async (params?: Partial<StorageLocationQuery>): Promise<StorageLocation[]> => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.set('page', String(params.page));
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.type) searchParams.set('type', params.type);
    if (params?.search) searchParams.set('search', params.search);
    if (params?.sortBy) searchParams.set('sortBy', params.sortBy);
    if (params?.sortOrder) searchParams.set('sortOrder', params.sortOrder);

    const query = searchParams.toString();
    const response = await api.get<StorageLocation[]>(`/storage-locations${query ? `?${query}` : ''}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch storage locations');
    }
    return response.data;
  },

  /**
   * Get a single storage location
   */
  get: async (id: string): Promise<StorageLocation> => {
    const response = await api.get<StorageLocation>(`/storage-locations/${id}`);
    if (!response.success || !response.data) {
      throw new Error('Failed to fetch storage location');
    }
    return response.data;
  },

  /**
   * Create a new storage location
   */
  create: async (data: CreateStorageLocationInput): Promise<StorageLocation> => {
    const response = await api.post<StorageLocation>('/storage-locations', data);
    if (!response.success || !response.data) {
      throw new Error('Failed to create storage location');
    }
    return response.data;
  },

  /**
   * Update an existing storage location
   */
  update: async (id: string, data: UpdateStorageLocationInput): Promise<StorageLocation> => {
    const response = await api.patch<StorageLocation>(`/storage-locations/${id}`, data);
    if (!response.success || !response.data) {
      throw new Error('Failed to update storage location');
    }
    return response.data;
  },

  /**
   * Delete a storage location
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/storage-locations/${id}`);
  },
};
